<script setup>
import { useRoute } from 'vue-router';
import { useTestRenderStore } from '@stores';
import { storeToRefs } from 'pinia';
import { computed, ref, watch, onMounted } from 'vue';
import { generateHTML } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { useI18n } from 'vue-i18n';
import { useToast } from 'primevue/usetoast';

import MultipleChoice from './components/Answers/MultipleChoice.vue';
import TextInput from './components/Answers/TextInput.vue';
import Sorting from './components/Answers/Sorting.vue';
import Matching from './components/Answers/Matching.vue';
import QuestionNavigation from './components/QuestionNavigation.vue';
import Timer from './components/Timer.vue';

const renderStore = useTestRenderStore();

const route = useRoute();
const toast = useToast();
const { t, locale } = useI18n({ useScope: 'global' });

const { question, currentQuestionId, questionsInfo, testConfig, selectedQuestion } = storeToRefs(renderStore);

const examId = route.query.exam_id;
const userAnswer = ref([]);
const isLoading = ref(true);

const isLastQuestion = computed(() => {
    return currentQuestionId.value === questionsInfo.value.at(-1)?.id;
});

const answerDisabled = computed(() => {
    const { checked, isCorrect } = selectedQuestion.value;
    const { lockAnswer, autoReveal } = testConfig.value;
    return (checked && lockAnswer) || (autoReveal && isCorrect !== null);
});

const questionNumber = computed(() => {
    return questionsInfo.value.findIndex((q) => q.id === currentQuestionId.value) + 1;
});

const currentComponent = computed(() => {
    if (!question.value) return null;
    switch (question.value.type) {
        case 1:
            return MultipleChoice;
        case 2:
            return MultipleChoice;
        case 3:
            return TextInput;
        case 4:
            return Matching;
        case 5:
            return Sorting;
        default:
            return null;
    }
});

const canSubmit = computed(() => {
    if (answerDisabled.value) return false;
    const { type } = question.value || {};
    const requiresInput = [1, 2, 3].includes(type);
    return !requiresInput || userAnswer.value?.length > 0;
});

const showNextBtn = computed(() => {
    return selectedQuestion.value.isCorrect !== null && !isLastQuestion.value && testConfig.value.autoReveal;
});

watch(
    () => testConfig.value?.language,
    (newLang) => {
        if (newLang) locale.value = newLang;
    },
    { immediate: true }
);

onMounted(async () => {
    await renderStore.getQuestionsInfo(examId);
    await renderStore.getExamQuestion(currentQuestionId.value);
    isLoading.value = false;
});

const submitAnswerHandler = async () => {
    const formattedData = question.value.type === 3 ? { answer: userAnswer.value } : { answers: Array.isArray(userAnswer.value) ? userAnswer.value : [userAnswer.value] };

    await renderStore.submitAnswer(formattedData);

    if (isLastQuestion.value && !testConfig.value.autoReveal) {
        await finishExamHandler();
    }
};
const finishExamHandler = async () => {
    if (testConfig.value.answersRequired) {
        const allAnswered = questionsInfo.value.every((q) => q.checked || q.isCorrect !== null);
        if (!allAnswered) {
            toast.add({
                severity: 'error',
                summary: t('tests.errors.AllQuestionsShouldBeAnswered'),
                life: 3000,
            });
            return;
        }
    }
    await renderStore.finishExam(examId);
};
</script>
<template>
    <Spinner v-if="isLoading" class="loading-wrapper" />
    <div v-else class="quiz-wrapper">
        <div class="quiz-container" @click.right.prevent @copy.prevent @paste.prevent>
            <QuestionNavigation />

            <div class="quiz-content">
                <div class="quiz-title">
                    <p class="quiz-question-number">{{ questionNumber }}/{{ questionsInfo.length }}:</p>
                    <div class="quiz-question" v-if="question.title" v-html="generateHTML(question.title, [StarterKit])"></div>
                </div>
                <component :is="currentComponent" :question="question" v-model="userAnswer" :disabled="answerDisabled" />
            </div>
            <Button class="quiz-btn" v-if="canSubmit" fluid :label="t('tests.confirm')" @click="submitAnswerHandler" />

            <Button class="quiz-btn" v-if="isLastQuestion && answerDisabled" fluid :label="t('tests.finish')" @click="finishExamHandler" />

            <Button class="quiz-btn" v-if="showNextBtn" fluid :label="t('tests.next')" @click="renderStore.goToNextQuestion" />
        </div>
        <Timer :totalTime="testConfig.totalTime" :timeLeft="testConfig.timeLeft" @timeEnd="renderStore.finishExam(examId)" />
    </div>

    <Toast />
</template>

<style scoped>
.loading-wrapper {
    @apply h-screen;
}
.quiz-wrapper {
    @apply flex items-start gap-16 w-full p-16 pt-24;
    user-select: none;
}
.quiz-container {
    @apply w-full;
}
.quiz-content {
    @apply flex flex-col gap-6 my-8;
}
.quiz-title {
    @apply flex gap-2 items-start;
}
.quiz-question {
    @apply text-2xl font-bold text-black;
}
.quiz-question-number {
    @apply text-2xl font-bold text-black;
}
.quiz-btn {
    @apply bg-[#3e83d0] border-[#3e83d0] hover:!bg-[#3e83d0] hover:!border-[#3e83d0];
}

/* Mobile adaptations */
@media (max-width: 768px) {
    .quiz-wrapper {
        @apply flex-col gap-0 p-4 pt-20;
    }

    .quiz-content {
        @apply gap-4 my-4;
    }

    .quiz-title {
        @apply flex-col gap-1;
    }

    .quiz-question {
        @apply text-lg;
    }

    .quiz-question-number {
        @apply text-lg;
    }
}

@media (max-width: 480px) {
    .quiz-wrapper {
        @apply p-3 pt-16;
    }

    .quiz-content {
        @apply gap-3 my-3;
    }

    .quiz-question {
        @apply text-base;
    }

    .quiz-question-number {
        @apply text-base;
    }
}
</style>
